<script setup lang="ts">
import { computed, ref } from 'vue'

const isExpanded = ref(false)
const viewMonday = ref(getMonday(new Date()))
const selectedWeekMonday = ref<Date | null>(null)

const emits = defineEmits<{
  (e: 'week-selected', payload: Date): void
}>()

const realTodayMonday = getMonday(new Date())
const daysLabels = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D']

const displayValue = computed(() => {
  if (!selectedWeekMonday.value) {
    return 'Selecionar'
  }
  const start = selectedWeekMonday.value.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  const endDate = new Date(selectedWeekMonday.value)
  endDate.setDate(endDate.getDate() + 6)
  const end = endDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  return `${start} a ${end}`
})

const weekDays = computed(() => {
  const days = []
  const tempDate = new Date(viewMonday.value)
  for (let i = 0; i < 7; i++) {
    const isToday = new Date().toDateString() === tempDate.toDateString()
    days.push({
      key: i,
      label: daysLabels[i],
      date: tempDate.getDate(),
      fullDate: new Date(tempDate),
      isToday: isToday,
      isWeekend: i >= 5
    })
    tempDate.setDate(tempDate.getDate() + 1)
  }
  return days
})

const monthYearLabel = computed(() => {
  return viewMonday.value.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  })
})

const isAtLimit = computed(() => {
  return viewMonday.value.getTime() >= realTodayMonday.getTime()
})

const isCurrentWeek = computed(() => {
    return viewMonday.value.getTime() === realTodayMonday.getTime()
})

function toggleSelector() {
  isExpanded.value = !isExpanded.value
}

function changeWeek(direction: -1 | 1) {
  const newDate = new Date(viewMonday.value)
  newDate.setDate(newDate.getDate() + direction * 7)
  viewMonday.value = newDate
}

function selectWeek() {
    if (isCurrentWeek.value) return
    
    selectedWeekMonday.value = new Date(viewMonday.value)

    const sunday = new Date(viewMonday.value)
    sunday.setDate(sunday.getDate() + 6)
    sunday.setHours(0, 0, 0, 0)

    emits('week-selected', sunday)
    
    isExpanded.value = false
}

function getMonday(d: Date): Date {
  d = new Date(d)
  d.setHours(0, 0, 0, 0)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

</script>

<template>
  <div @focus="isExpanded = true" class="relative w-full max-w-md">
    <div
      tabindex="0"
      @click="toggleSelector"
      @blur="isExpanded = false"
      class="relative w-full cursor-pointer border border-zinc-300 bg-white py-3 pl-4 pr-10 text-left transition-colors hover:border-zinc-400 focus:outline-none"
    >
      <span class="text-slate-600">{{ displayValue }}</span>
      <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <svg
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          class="h-4 w-4 transform text-zinc-400 transition-transform duration-200 ease-in-out"
          :class="isExpanded ? 'rotate-180' : 'rotate-0'"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- O painel do seletor de semana -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      leave-active-class="transition-all duration-150 ease-in"
      enter-from-class="transform -translate-y-2 opacity-0"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div
        v-if="isExpanded"
        class="absolute top-full left-0 z-10 mt-0 w-full border border-slate-200 bg-white p-4 shadow-lg"
      >
        <!-- Cabeçalho do seletor -->
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-md capitalize font-bold text-slate-800">
            {{ monthYearLabel }}
          </h3>
          <div class="flex gap-1">
            <button type="button" @mousedown.prevent="changeWeek(-1)" class="border border-slate-200 p-1.5 text-slate-500 transition-all hover:bg-slate-100">
              <i class="pi pi-angle-left text-xs"></i>
            </button>
            <button type="button" @mousedown.prevent="changeWeek(1)" :disabled="isAtLimit" class="border border-slate-200 p-1.5 text-slate-500 transition-all hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30">
              <i class="pi pi-angle-right text-xs"></i>
            </button>
          </div>
        </div>

        <!-- Grid da semana com alinhamento corrigido -->
        <div class="grid grid-cols-7 gap-1 text-center">
          <div v-for="day in weekDays" :key="day.key" class="flex flex-col items-center">
            <span class="mb-2 text-xs font-bold uppercase text-slate-400">{{ day.label }}</span>
            <div
              class="flex h-9 w-9 items-center justify-center border border-transparent text-sm font-semibold"
              :class="{
                'bg-slate-800 text-white': day.isToday,
                'text-slate-700': !day.isToday,
                'text-red-500': day.isWeekend && !day.isToday
              }"
            >
              {{ day.date }}
            </div>
          </div>
        </div>

        <!-- Botão de Seleção -->
        <div v-if="isCurrentWeek" class="mt-4 border-t border-slate-200 pt-4 text-center">
          <p class="text-sm text-slate-600">
            O relatório de anomalias da semana atual estará disponível apenas na próxima semana. Por favor, selecione um período anterior.
          </p>
        </div>
        <div v-else class="mt-4 border-t border-slate-200 pt-4">
          <button
            @click="selectWeek"
            class="w-full bg- py-2 px-4 text-sm font-medium bg-blue-600 border-blue-600 text-white transition-colors hover:bg-slate-700"
          >
            Selecionar Semana
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>